import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  BookingSchema,
  BookingCreateInputSchema,
  BookingUpdateInputSchema,
  type Booking,
  type BookingCreateInput,
  type BookingUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `booking` (créneau daté, org-wide).
 *
 * Même patron que `asset` : l'entrée est VALIDÉE par Zod
 * (`BookingCreateInputSchema`) AVANT toute écriture Firestore. Les liens vers
 * d'autres familles (contact, worker, resource, order) sont de simples ids.
 */

export type { BookingCreateInput };

/** Nom de la collection Firestore de la famille booking. */
export const BOOKINGS_COLLECTION = 'bookings';

/**
 * Repository typé pour la famille `booking`.
 * Toute lecture est validée par `BookingSchema`, toute écriture par
 * `BookingCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface BookingRepository {
  create(input: BookingCreateInput): Promise<Booking>;
  list(options?: ListOptions): Promise<Booking[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Booking | null>;
  update(id: string, patch: BookingUpdateInput): Promise<Booking>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Booking>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Booking>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Booking>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const bookingConverter = createConverter<Booking>((raw: DocumentData) => BookingSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createBookingRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): BookingRepository {
  const col = collection(db, BOOKINGS_COLLECTION).withConverter(bookingConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Booking>(db, col, BOOKINGS_COLLECTION, getActorId);

  return {
    async create(input: BookingCreateInput): Promise<Booking> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = BookingCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.workerId !== undefined) optional['workerId'] = fields.workerId;
      if (fields.resourceIds !== undefined) optional['resourceIds'] = fields.resourceIds;
      if (fields.startAt !== undefined) optional['startAt'] = fields.startAt;
      if (fields.endAt !== undefined) optional['endAt'] = fields.endAt;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.source !== undefined) optional['source'] = fields.source;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Booking);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (startAt/endAt sont déjà des
      // Date issues de l'entrée validée).
      return BookingSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Booking[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Booking | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: BookingUpdateInput): Promise<Booking> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = BookingUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, BOOKINGS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`booking ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
